import React, { useState, useEffect, useRef } from 'react';

/* ─────────────────────────────────────────────
   DESIGN TOKENS — clean, minimal dark
───────────────────────────────────────────── */
const T = {
  bg:         '#080b14',
  surface:    '#0e1420',
  surfaceHi:  '#141c2c',
  border:     '#1a2540',
  borderHi:   '#243358',
  text:       '#f0f4ff',
  textSub:    '#7a90b8',
  textMute:   '#3d5070',
  emerald:    '#10b981',
  emeraldDim: 'rgba(16,185,129,0.08)',
  blue:       '#3b82f6',
  amber:      '#f59e0b',
  red:        '#ef4444',
  purple:     '#a78bfa',
  green:      '#22c55e',
};

/* ─────────────────────────────────────────────
   HOOKS
───────────────────────────────────────────── */
function useCountUp(target, duration = 2000) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStarted(true); }, { threshold: 0.3 });
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);
  useEffect(() => {
    if (!started) return;
    let s = 0;
    const inc = target / (duration / 16);
    const t = setInterval(() => {
      s += inc;
      if (s >= target) { setCount(target); clearInterval(t); }
      else setCount(Math.floor(s));
    }, 16);
    return () => clearInterval(t);
  }, [started, target, duration]);
  return [count, ref];
}

function useReveal(delay = 0) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const fb = setTimeout(() => setVis(true), 1000 + delay);
    const o = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVis(true); clearTimeout(fb); }
    }, { threshold: 0, rootMargin: '0px 0px -40px 0px' });
    if (ref.current) o.observe(ref.current);
    return () => { o.disconnect(); clearTimeout(fb); };
  }, [delay]);
  return [ref, vis];
}

/* ─────────────────────────────────────────────
   LOGO
───────────────────────────────────────────── */
function LogoMark({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <rect width="32" height="32" rx="8" fill="#0a1020" stroke="#10b981" strokeWidth="1.2"/>
      <polyline points="3,17 7,17 9,17 11,11 13,23 15,16 17,16 19,13 21,18 23,16 26,16 29,14" fill="none" stroke="#10b981" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="29" cy="14" r="1.8" fill="#3b82f6"/>
      <line x1="3" y1="21.5" x2="29" y2="21.5" stroke="#10b981" strokeWidth="0.5" strokeOpacity="0.2"/>
      <rect x="4"  y="26" width="2.5" height="4"   rx="0.8" fill="#10b981" fillOpacity="0.3"/>
      <rect x="8"  y="25" width="2.5" height="5"   rx="0.8" fill="#10b981" fillOpacity="0.45"/>
      <rect x="12" y="24" width="2.5" height="6"   rx="0.8" fill="#10b981" fillOpacity="0.6"/>
      <rect x="16" y="23" width="2.5" height="7"   rx="0.8" fill="#10b981" fillOpacity="0.75"/>
      <rect x="20" y="22" width="2.5" height="8"   rx="0.8" fill="#10b981" fillOpacity="0.9"/>
      <rect x="24" y="21.5" width="2.5" height="8.5" rx="0.8" fill="#10b981"/>
    </svg>
  );
}

/* ─────────────────────────────────────────────
   MAIN
───────────────────────────────────────────── */

const tickerDrugs = ['HUMIRA · AbbVie','KEYTRUDA · Merck','ELIQUIS · BMS','OZEMPIC · Novo Nordisk','SKYRIZI · AbbVie','DUPIXENT · Sanofi','XARELTO · Bayer','JARDIANCE · Boehringer','ENTRESTO · Novartis','TALTZ · Eli Lilly','TREMFYA · J&J','IMBRUVICA · AbbVie','VENCLEXTA · AbbVie','LYNPARZA · AstraZeneca'];

export default function LandingPage({ onEnterDashboard, onSignIn, onNavigate }) {
  const [scrolled, setScrolled]     = useState(false);
  const [heroVis,  setHeroVis]      = useState(false);
  
  const [w, setW]                   = useState(window.innerWidth);
  const isMobile = w < 768;

  const [c1,r1]=useCountUp(1000), [c2,r2]=useCountUp(1000), [c3,r3]=useCountUp(2000), [c4,r4]=useCountUp(160);
  const [rHow,vHow]=useReveal(0), [rSuite,vSuite]=useReveal(0), [rFeat,vFeat]=useReveal(0);
  const [rComp,vComp]=useReveal(0), [rProof,vProof]=useReveal(0), [rPrice,vPrice]=useReveal(0);

  useEffect(() => {
    setTimeout(() => setHeroVis(true), 80);
    const onScroll = () => setScrolled(window.scrollY > 30);
    const onResize = () => setW(window.innerWidth);
    window.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onResize); };
  }, []);

  const go = id => { document.getElementById(id)?.scrollIntoView({ behavior:'smooth' }); ; };

  /* ── data ── */
  const steps = [
    { n:'01', title:'We monitor everything',   color:T.emerald, desc:'FDA pipeline, 1,000+ clinical trials across 8 disease areas, SEC insider trades, earnings dates, and PDUFA calendars — refreshed automatically.' },
    { n:'02', title:'AI explains it clearly',  color:T.blue,    desc:'Every drug, trial, trade, and FDA rejection gets a plain-English SUMMARY, RISK score with reasoning, and actionable INSIGHT.' },
    { n:'03', title:'You decide with confidence', color:T.purple, desc:'Search, filter, watchlist, and track. Countdown timers, earnings collisions, post-mortems — the full picture before the market reacts.' },
  ];

  const suite = [
    { title:'Insider Trading Layer',     accent:T.purple, desc:"160+ real SEC Form 4 trades tracked with AI analysis. When executives buy their own stock with conviction, it's a signal. We decode it.",   stat:'160+',  statLabel:'SEC Trades'         },
    { title:'FDA Failure Post-Mortems',  accent:T.red,    desc:'Every FDA Complete Response Letter dissected by AI. Understand exactly what failed, the financial damage, and the path to resubmission.',  stat:'8',     statLabel:'CRL Analyses'        },
    { title:'Earnings × FDA Collider',   accent:T.amber,  desc:'The most volatile week in biotech: when an earnings call and an FDA decision converge. We detect these automatically and alert you.',       stat:'46',    statLabel:'Earnings Tracked'    },
    { title:'Portfolio Tracker',         accent:T.emerald,desc:'Track your biotech holdings with live Polygon.io data. See how FDA events, insider trades, and trial outcomes affect your real positions.',  stat:'Live',  statLabel:'Polygon.io Prices'   },
  ];

  const features = [
    { title:'FDA Catalyst Tracking',       accent:T.blue,   desc:'Every approval and rejection explained in plain English. Know what it means before the market reacts.' },
    { title:'Clinical Trial Intelligence', accent:T.purple, desc:'1,000+ Phase 1–3 trials across oncology, cardiology, neurology and more. Spot market-moving events early.' },
    { title:'AI Investment Analysis',      accent:T.emerald,desc:'Complex biotech data distilled into SUMMARY, RISK, and INSIGHT. No PhD required.' },
    { title:'FDA Calendar & Timing',       accent:T.blue,   desc:'55+ PDUFA dates with live countdowns. Know when decisions drop days before the market wakes up.' },
    { title:'Insider Trading Signals',     accent:T.purple, isNew:true, desc:'SEC Form 4 filings decoded. Insider buying is bullish. Selling tells a story. We do the reading.' },
    { title:'FDA Failure Post-Mortems',    accent:T.red,    isNew:true, desc:'When the FDA says no, we explain why — and what a recovery path looks like. Full CRL analysis.' },
    { title:'Earnings × FDA Collisions',   accent:T.amber,  isNew:true, desc:'Detect the most volatile biotech week: when earnings and FDA decisions converge in the same week.' },
    { title:'Portfolio Tracker',           accent:T.emerald,isNew:true, desc:'Real-time portfolio with Polygon.io data. Visualize your exposure to every catalyst in the pipeline.' },
  ];

  const comparison = [
    { f:'FDA Pipeline Data',            us:true,  bl:true,  sa:false },
    { f:'Clinical Trial Tracking',      us:true,  bl:true,  sa:false },
    { f:'AI Investment Summaries',      us:true,  bl:false, sa:false },
    { f:'Insider Trading Intelligence', us:true,  bl:true,  sa:false },
    { f:'FDA Failure Post-Mortems',     us:true,  bl:false, sa:false },
    { f:'Earnings × FDA Collisions',    us:true,  bl:false, sa:false },
    { f:'Portfolio Tracker',            us:true,  bl:true,  sa:true  },
    { f:'Risk Scoring with Reasoning',  us:true,  bl:false, sa:false },
    { f:'Built for Retail Investors',   us:true,  bl:false, sa:true  },
    { f:'Monthly Price',                us:'$19', bl:'$2,083', sa:'$35' },
  ];

  const testimonials = [
    { q:"I used to spend hours reading FDA press releases. AlphaBioAI gives me the same insights in seconds.", name:"Michael R.", role:"Retail Biotech Investor", initials:"MR", accent:T.emerald },
    { q:"The insider trading layer changed everything. I can see when executives are loading up on their own stock weeks before the move.", name:"Sarah K.", role:"Self-directed Investor", initials:"SK", accent:T.purple },
    { q:"Bloomberg costs $25K/year at my old firm. AlphaBioAI delivers 80% of the value at 1% of the price.", name:"James T.", role:"Former Hedge Fund Analyst", initials:"JT", accent:T.blue },
  ];

  const px = isMobile ? '20px' : '48px';
  const reveal = (vis, delay = 0) => ({ opacity: vis ? 1 : 0, transform: vis ? 'translateY(0)' : 'translateY(28px)', transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms` });

  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */
  return (
    <div style={{ minHeight:'100vh', backgroundColor:T.bg, color:T.text, fontFamily:"'Inter',system-ui,sans-serif", overflowX:'hidden' }}>

      {/* Subtle background gradient */}
      <div style={{ position:'fixed', inset:0, background:'radial-gradient(ellipse 80% 50% at 50% -10%, rgba(16,185,129,0.06) 0%, transparent 60%)', pointerEvents:'none', zIndex:0 }} />

      {/* ── NAV ── */}
      <nav style={{ position:'fixed',top:0,left:0,right:0,zIndex:200,height:'60px',display:'flex',alignItems:'center',justifyContent:'space-between',padding:isMobile?'0 20px':'0 48px',backgroundColor:scrolled?'rgba(8,11,20,0.92)':'transparent',borderBottom:scrolled?`1px solid ${T.border}`:'1px solid transparent',backdropFilter:scrolled?'blur(16px)':'none',transition:'all 0.3s' }}>
        <div onClick={() => window.scrollTo({top:0,behavior:'smooth'})} style={{ display:'flex',alignItems:'center',gap:'10px',cursor:'pointer',userSelect:'none' }}>
          <LogoMark size={30} />
          <span style={{ fontSize:'16px',fontWeight:'700',color:T.text,letterSpacing:'-0.3px' }}>Alpha<span style={{ color:T.emerald }}>Bio</span>AI</span>
        </div>

        {!isMobile && (
          <div style={{ display:'flex',gap:'4px' }}>
            {[{label:'How It Works',id:'howitworks'},{label:'Features',id:'features'},{label:'Compare',id:'compare'},{label:'Pricing',id:'pricing'}].map(item => (
              <button key={item.label} onClick={() => go(item.id)} style={{ background:'none',border:'none',color:T.textSub,cursor:'pointer',fontSize:'14px',fontWeight:'500',padding:'8px 14px',borderRadius:'8px',transition:'color 0.2s' }} onMouseEnter={e=>e.currentTarget.style.color=T.text} onMouseLeave={e=>e.currentTarget.style.color=T.textSub}>{item.label}</button>
            ))}
          </div>
        )}

        <div style={{ display:'flex',gap:'8px',alignItems:'center' }}>
          <button onClick={onSignIn} style={{ background:'none',border:'none',color:T.textSub,cursor:'pointer',fontSize:'14px',fontWeight:'500',padding:'8px 12px',borderRadius:'8px',transition:'color 0.2s' }} onMouseEnter={e=>e.currentTarget.style.color=T.text} onMouseLeave={e=>e.currentTarget.style.color=T.textSub}>Sign in</button>
          <button onClick={onEnterDashboard} style={{ backgroundColor:T.emerald,color:'white',border:'none',padding:'9px 20px',borderRadius:'8px',cursor:'pointer',fontWeight:'600',fontSize:'14px',transition:'opacity 0.2s' }} onMouseEnter={e=>e.currentTarget.style.opacity='0.85'} onMouseLeave={e=>e.currentTarget.style.opacity='1'}>
            {isMobile ? 'Try free' : 'Start free trial'}
          </button>
        </div>
      </nav>

      {/* ── HERO ── */}
      <div style={{ position:'relative',zIndex:1,maxWidth:'900px',margin:'0 auto',padding:isMobile?'120px 24px 48px':'160px 48px 56px',textAlign:'center' }}>

        {/* Brand mark — hero centrepiece */}
        <div style={{ ...reveal(heroVis), display:'flex',flexDirection:'column',alignItems:'center',gap:'12px',marginBottom:'44px',transitionDelay:'0ms' }}>
          <LogoMark size={isMobile?64:80} />
          <div>
            <div style={{ fontSize:isMobile?'28px':'36px',fontWeight:'800',color:T.text,letterSpacing:'-1px',lineHeight:1.1,textAlign:'center' }}>
              Alpha<span style={{ color:T.emerald }}>Bio</span><span style={{ fontWeight:'300',color:T.textSub }}>AI</span>
            </div>
            <div style={{ fontSize:'11px',color:T.textSub,letterSpacing:'3px',textAlign:'center',marginTop:'4px',fontWeight:'600' }}>INTELLIGENCE</div>
          </div>
        </div>

        {/* Headline */}
        <h1 style={{ ...reveal(heroVis), fontSize:isMobile?'40px':'76px', fontWeight:'800', lineHeight:1.08, letterSpacing:isMobile?'-1.5px':'-3px', margin:`0 0 ${isMobile?'20px':'28px'}`, color:T.text, transitionDelay:'80ms' }}>
          Institutional biotech<br />
          intelligence, <span style={{ color:T.emerald }}>finally at</span><br />
          retail price.
        </h1>

        {/* Sub */}
        <p style={{ ...reveal(heroVis), fontSize:isMobile?'16px':'19px', color:T.textSub, lineHeight:1.75, maxWidth:'580px', margin:`0 auto ${isMobile?'36px':'52px'}`, transitionDelay:'160ms' }}>
          Track every FDA decision, decode insider trades, and analyze clinical trials — with AI that explains what it means for your portfolio. Starting at <strong style={{ color:T.text, fontWeight:'600' }}>$19/month</strong>.
        </p>

        {/* CTA */}
        <div style={{ ...reveal(heroVis), display:'flex',gap:'12px',justifyContent:'center',flexWrap:'wrap',marginBottom:'28px',transitionDelay:'240ms' }}>
          <button onClick={onEnterDashboard} style={{ backgroundColor:T.emerald,color:'white',border:'none',padding:isMobile?'14px 32px':'15px 36px',borderRadius:'10px',cursor:'pointer',fontWeight:'700',fontSize:isMobile?'15px':'16px',transition:'opacity 0.2s',width:isMobile?'100%':'auto' }} onMouseEnter={e=>e.currentTarget.style.opacity='0.85'} onMouseLeave={e=>e.currentTarget.style.opacity='1'}>
            Start 7-day free trial →
          </button>
          {!isMobile && (
            <button onClick={onEnterDashboard} style={{ backgroundColor:'transparent',color:T.textSub,border:`1px solid ${T.border}`,padding:'15px 28px',borderRadius:'10px',cursor:'pointer',fontWeight:'500',fontSize:'16px',transition:'all 0.2s' }} onMouseEnter={e=>{e.currentTarget.style.borderColor=T.borderHi;e.currentTarget.style.color=T.text;}} onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.color=T.textSub;}}>
              View demo
            </button>
          )}
        </div>

        {/* Trust line */}
        <div style={{ ...reveal(heroVis), display:'flex',gap:isMobile?'14px':'32px',justifyContent:'center',flexWrap:'wrap',transitionDelay:'320ms' }}>
          {['No charge for 7 days','Cancel anytime','No credit card friction'].map((t,i) => (
            <span key={i} style={{ fontSize:'13px',color:T.textMute,display:'flex',alignItems:'center',gap:'6px' }}>
              <span style={{ color:T.emerald }}>✓</span>{t}
            </span>
          ))}
        </div>
      </div>

      {/* ── PRODUCT DEMO ── */}
      <div style={{ position:'relative', zIndex:1, maxWidth:'1140px', margin:'0 auto', padding: isMobile ? '0 20px 72px' : '0 24px 96px' }}>
        <div style={{ borderRadius:'16px', overflow:'hidden', border:`1px solid ${T.border}`, height: isMobile ? '420px' : '560px', boxShadow:'0 0 80px rgba(16,185,129,0.06)' }}>
          <iframe
            src="/alphabioai-demo.html"
            title="AlphaBioAI Product Demo"
            style={{ width:'100%', height:'100%', border:'none', display:'block' }}
          />
        </div>
        <div style={{ textAlign:'center', marginTop:'16px', fontSize:'13px', color: T.textMute }}>
          Live product walkthrough — auto-plays through every feature
        </div>
      </div>

      {/* ── TICKER STRIP ── */}
      <div style={{ position:'relative',zIndex:1,borderTop:`1px solid ${T.border}`,borderBottom:`1px solid ${T.border}`,backgroundColor:'rgba(8,11,20,0.9)',padding:'11px 0',overflow:'hidden',marginBottom:'0' }}>
        <div style={{ display:'flex',gap:'56px',whiteSpace:'nowrap',animation:'ticker 40s linear infinite',width:'max-content' }}>
          {[...tickerDrugs,...tickerDrugs].map((drug,i) => (
            <span key={i} style={{ fontSize:'12px',color:T.textMute,fontWeight:'600',letterSpacing:'0.5px' }}>
              <span style={{ color:T.emerald,marginRight:'8px',fontSize:'7px' }}>◆</span>{drug}
            </span>
          ))}
        </div>
      </div>

      {/* ── STATS ── */}
      <div style={{ position:'relative',zIndex:1,maxWidth:'960px',margin:'0 auto',padding:`0 ${px} 96px` }}>
        <div style={{ display:'grid',gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(4,1fr)',gap:'1px',backgroundColor:T.border,borderRadius:'16px',overflow:'hidden',border:`1px solid ${T.border}` }}>
          {[
            {ref:r1,val:c1,suffix:'+',label:'FDA Drug Records',  color:T.blue   },
            {ref:r2,val:c2,suffix:'+',label:'Clinical Trials',   color:T.purple },
            {ref:r3,val:c3,suffix:'+',label:'AI Summaries',      color:T.emerald},
            {ref:r4,val:c4,suffix:'+',label:'Insider Trades',    color:T.amber  },
          ].map((s,i) => (
            <div key={i} ref={s.ref} style={{ backgroundColor:T.surface,padding:isMobile?'28px 16px':'36px 24px',textAlign:'center' }}>
              <div style={{ fontSize:isMobile?'30px':'42px',fontWeight:'800',color:s.color,letterSpacing:'-1px',lineHeight:1 }}>{s.val.toLocaleString()}{s.suffix}</div>
              <div style={{ fontSize:'13px',color:T.textSub,marginTop:'6px',fontWeight:'500' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── HOW IT WORKS ── */}
      <div id="howitworks" ref={rHow} style={{ position:'relative',zIndex:1,maxWidth:'1040px',margin:'0 auto',padding:`0 ${px} 96px` }}>
        <div style={{ textAlign:'center',marginBottom:'56px' }}>
          <p style={{ fontSize:'12px',fontWeight:'700',color:T.emerald,letterSpacing:'1.5px',textTransform:'uppercase',marginBottom:'14px' }}>How it works</p>
          <h2 style={{ ...reveal(vHow),fontSize:isMobile?'30px':'48px',fontWeight:'800',letterSpacing:isMobile?'-1px':'-2px',color:T.text,margin:'0 0 14px' }}>From data to decision<br />in seconds</h2>
          <p style={{ ...reveal(vHow,80),color:T.textSub,fontSize:isMobile?'15px':'18px',maxWidth:'440px',margin:'0 auto',lineHeight:1.7 }}>Three steps between raw FDA data and your investment edge.</p>
        </div>
        <div style={{ display:'grid',gridTemplateColumns:isMobile?'1fr':'repeat(3,1fr)',gap:'16px' }}>
          {steps.map((s,i) => (
            <div key={i} style={{ ...reveal(vHow,i*80+160),backgroundColor:T.surface,border:`1px solid ${T.border}`,borderRadius:'16px',padding:isMobile?'28px 24px':'36px 32px',position:'relative' }}>
              <div style={{ width:'36px',height:'36px',borderRadius:'10px',backgroundColor:`${s.color}12`,border:`1px solid ${s.color}20`,display:'flex',alignItems:'center',justifyContent:'center',marginBottom:'20px' }}>
                <span style={{ fontSize:'12px',fontWeight:'800',color:s.color }}>{s.n}</span>
              </div>
              <h3 style={{ fontSize:'17px',fontWeight:'700',color:T.text,margin:'0 0 12px',letterSpacing:'-0.2px' }}>{s.title}</h3>
              <p style={{ fontSize:'14px',color:T.textSub,lineHeight:1.75,margin:0 }}>{s.desc}</p>
              <div style={{ position:'absolute',top:0,left:'24px',width:'40px',height:'2px',backgroundColor:s.color,borderRadius:'1px' }} />
            </div>
          ))}
        </div>
      </div>

      {/* ── INTELLIGENCE SUITE ── */}
      <div ref={rSuite} style={{ position:'relative',zIndex:1,maxWidth:'1040px',margin:'0 auto',padding:`0 ${px} 96px` }}>
        <div style={{ textAlign:'center',marginBottom:'56px' }}>
          <div style={{ display:'inline-flex',alignItems:'center',gap:'6px',backgroundColor:T.emeraldDim,border:`1px solid rgba(16,185,129,0.15)`,color:T.emerald,padding:'5px 14px',borderRadius:'100px',fontSize:'11px',fontWeight:'700',marginBottom:'16px',letterSpacing:'0.5px' }}>
            Just shipped
          </div>
          <h2 style={{ ...reveal(vSuite),fontSize:isMobile?'28px':'46px',fontWeight:'800',letterSpacing:isMobile?'-1px':'-2px',color:T.text,margin:'0 0 12px' }}>The Intelligence Suite</h2>
          <p style={{ ...reveal(vSuite,80),color:T.textSub,fontSize:isMobile?'15px':'18px',maxWidth:'440px',margin:'0 auto' }}>Four modules that go beyond FDA tracking.</p>
        </div>
        <div style={{ display:'grid',gridTemplateColumns:isMobile?'1fr':'repeat(2,1fr)',gap:'14px' }}>
          {suite.map((s,i) => (
            <div key={i} style={{ ...reveal(vSuite,i*80+160),backgroundColor:T.surface,border:`1px solid ${T.border}`,borderRadius:'16px',padding:isMobile?'28px 24px':'36px',position:'relative',overflow:'hidden' }}>
              <div style={{ position:'absolute',top:0,left:0,right:0,height:'2px',backgroundColor:s.accent,opacity:0.5 }} />
              <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'16px' }}>
                <span style={{ fontSize:'11px',fontWeight:'700',color:T.emerald,backgroundColor:T.emeraldDim,border:`1px solid rgba(16,185,129,0.15)`,padding:'3px 10px',borderRadius:'100px' }}>NEW</span>
              </div>
              <h3 style={{ fontSize:isMobile?'17px':'20px',fontWeight:'700',color:T.text,margin:'0 0 10px',letterSpacing:'-0.3px' }}>{s.title}</h3>
              <p style={{ fontSize:'14px',color:T.textSub,lineHeight:1.75,margin:'0 0 22px' }}>{s.desc}</p>
              <div style={{ display:'flex',alignItems:'center',gap:'14px' }}>
                <div style={{ backgroundColor:`${s.accent}0d`,border:`1px solid ${s.accent}20`,borderRadius:'8px',padding:'8px 16px' }}>
                  <div style={{ fontSize:'20px',fontWeight:'800',color:s.accent,lineHeight:1 }}>{s.stat}</div>
                  <div style={{ fontSize:'11px',color:s.accent,opacity:0.7,marginTop:'2px',fontWeight:'600' }}>{s.statLabel}</div>
                </div>
                <button onClick={onEnterDashboard} style={{ backgroundColor:'transparent',border:`1px solid ${T.border}`,color:T.textSub,padding:'9px 18px',borderRadius:'8px',cursor:'pointer',fontSize:'14px',fontWeight:'500',transition:'all 0.2s' }} onMouseEnter={e=>{e.currentTarget.style.borderColor=s.accent;e.currentTarget.style.color=s.accent;}} onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.color=T.textSub;}}>
                  Explore →
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── FEATURES ── */}
      <div id="features" ref={rFeat} style={{ position:'relative',zIndex:1,maxWidth:'1040px',margin:'0 auto',padding:`0 ${px} 96px` }}>
        <div style={{ textAlign:'center',marginBottom:'56px' }}>
          <p style={{ fontSize:'12px',fontWeight:'700',color:T.emerald,letterSpacing:'1.5px',textTransform:'uppercase',marginBottom:'14px' }}>Features</p>
          <h2 style={{ ...reveal(vFeat),fontSize:isMobile?'28px':'46px',fontWeight:'800',letterSpacing:isMobile?'-1px':'-2px',color:T.text,margin:'0 0 12px' }}>Everything you need<br />to invest smarter</h2>
          <p style={{ ...reveal(vFeat,80),color:T.textSub,fontSize:isMobile?'15px':'18px',maxWidth:'400px',margin:'0 auto' }}>Professional biotech tools built for retail investors.</p>
        </div>
        <div style={{ display:'grid',gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(4,1fr)',gap:'10px' }}>
          {features.map((f,i) => (
            <div key={i} style={{ ...reveal(vFeat,i*50+160),backgroundColor:T.surface,border:`1px solid ${T.border}`,borderRadius:'14px',padding:isMobile?'20px 16px':'26px 22px',position:'relative',transition:'border-color 0.2s' }} onMouseEnter={e=>e.currentTarget.style.borderColor=T.borderHi} onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}>
              {f.isNew && <div style={{ position:'absolute',top:'10px',right:'10px',fontSize:'9px',fontWeight:'700',color:T.emerald,backgroundColor:T.emeraldDim,border:`1px solid rgba(16,185,129,0.15)`,padding:'2px 6px',borderRadius:'4px' }}>NEW</div>}
              <div style={{ width:'6px',height:'6px',borderRadius:'50%',backgroundColor:f.accent,marginBottom:'14px' }} />
              <h3 style={{ margin:'0 0 8px',color:T.text,fontSize:isMobile?'13px':'15px',fontWeight:'700',letterSpacing:'-0.1px' }}>{f.title}</h3>
              <p style={{ margin:0,color:T.textSub,fontSize:isMobile?'12px':'13px',lineHeight:1.7 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── COMPARISON ── */}
      <div id="compare" ref={rComp} style={{ position:'relative',zIndex:1,maxWidth:'860px',margin:'0 auto',padding:`0 ${px} 96px` }}>
        <div style={{ textAlign:'center',marginBottom:'56px' }}>
          <p style={{ fontSize:'12px',fontWeight:'700',color:T.emerald,letterSpacing:'1.5px',textTransform:'uppercase',marginBottom:'14px' }}>Compare</p>
          <h2 style={{ ...reveal(vComp),fontSize:isMobile?'28px':'46px',fontWeight:'800',letterSpacing:isMobile?'-1px':'-2px',color:T.text,margin:'0 0 12px' }}>How we stack up</h2>
          <p style={{ ...reveal(vComp,80),color:T.textSub,fontSize:isMobile?'15px':'18px' }}>Professional intelligence. Without the institutional price tag.</p>
        </div>
        <div style={{ ...reveal(vComp,160),backgroundColor:T.surface,borderRadius:'14px',border:`1px solid ${T.border}`,overflow:'hidden' }}>
          <div style={{ display:'grid',gridTemplateColumns:isMobile?'1.8fr 1fr 1fr 1fr':'2fr 1fr 1fr 1fr',backgroundColor:T.surfaceHi,padding:isMobile?'12px 16px':'18px 28px',borderBottom:`1px solid ${T.border}` }}>
            <div style={{ fontSize:'11px',color:T.textMute,fontWeight:'700',letterSpacing:'1px',textTransform:'uppercase' }}>Feature</div>
            {[{n:'AlphaBioAI',h:true},{n:isMobile?'Bloomberg':'Bloomberg Terminal'},{n:isMobile?'Seeking α':'Seeking Alpha'}].map((c,i) => (
              <div key={i} style={{ textAlign:'center' }}>
                <span style={{ fontSize:isMobile?'12px':'14px',fontWeight:'700',color:c.h?T.emerald:T.textSub,backgroundColor:c.h?T.emeraldDim:'transparent',padding:c.h?'3px 10px':'0',borderRadius:c.h?'100px':'0',border:c.h?`1px solid rgba(16,185,129,0.2)`:'none' }}>{c.n}</span>
              </div>
            ))}
          </div>
          {comparison.map((row,i) => (
            <div key={i} style={{ display:'grid',gridTemplateColumns:isMobile?'1.8fr 1fr 1fr 1fr':'2fr 1fr 1fr 1fr',padding:isMobile?'11px 16px':'15px 28px',borderBottom:i<comparison.length-1?`1px solid rgba(26,37,64,0.8)`:'none',backgroundColor:i%2===0?'transparent':'rgba(255,255,255,0.01)' }}>
              <div style={{ fontSize:isMobile?'12px':'14px',color:T.text,fontWeight:'500' }}>{row.f}</div>
              {[row.us,row.bl,row.sa].map((v,j) => (
                <div key={j} style={{ textAlign:'center',fontSize:isMobile?'14px':'17px',fontWeight:'700' }}>
                  {typeof v === 'boolean'
                    ? <span style={{ color:v?(j===0?T.emerald:T.textSub):T.textMute }}>{v?'✓':'✕'}</span>
                    : <span style={{ fontSize:isMobile?'12px':'14px',color:j===0?T.emerald:T.text,fontWeight:'700' }}>{v}</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ── TESTIMONIALS ── */}
      <div ref={rProof} style={{ position:'relative',zIndex:1,maxWidth:'1040px',margin:'0 auto',padding:`0 ${px} 96px` }}>
        <div style={{ textAlign:'center',marginBottom:'56px' }}>
          <p style={{ fontSize:'12px',fontWeight:'700',color:T.emerald,letterSpacing:'1.5px',textTransform:'uppercase',marginBottom:'14px' }}>Testimonials</p>
          <h2 style={{ ...reveal(vProof),fontSize:isMobile?'26px':'46px',fontWeight:'800',letterSpacing:isMobile?'-0.5px':'-2px',color:T.text,margin:0 }}>Trusted by biotech investors</h2>
        </div>
        <div style={{ display:'grid',gridTemplateColumns:isMobile?'1fr':'repeat(3,1fr)',gap:'14px' }}>
          {testimonials.map((t,i) => (
            <div key={i} style={{ ...reveal(vProof,i*80+160),backgroundColor:T.surface,border:`1px solid ${T.border}`,borderRadius:'16px',padding:isMobile?'26px 22px':'32px',position:'relative',transition:'border-color 0.2s' }} onMouseEnter={e=>e.currentTarget.style.borderColor=T.borderHi} onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}>
              <div style={{ position:'absolute',top:0,left:'20px',right:'20px',height:'1px',backgroundColor:t.accent,opacity:0.2 }} />
              <p style={{ color:'#c8deff',fontSize:'14px',lineHeight:1.8,margin:'0 0 20px',fontStyle:'italic' }}>"{t.q}"</p>
              <div style={{ display:'flex',alignItems:'center',gap:'10px' }}>
                <div style={{ width:'38px',height:'38px',borderRadius:'50%',backgroundColor:`${t.accent}20`,border:`1px solid ${t.accent}30`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'13px',fontWeight:'700',color:t.accent,flexShrink:0 }}>{t.initials}</div>
                <div>
                  <div style={{ fontSize:'14px',fontWeight:'700',color:T.text }}>{t.name}</div>
                  <div style={{ fontSize:'12px',color:T.textMute,marginTop:'1px' }}>{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── PRICING ── */}
      <div id="pricing" ref={rPrice} style={{ position:'relative',zIndex:1,maxWidth:'820px',margin:'0 auto',padding:`0 ${px} 96px`,textAlign:'center' }}>
        <p style={{ fontSize:'12px',fontWeight:'700',color:T.emerald,letterSpacing:'1.5px',textTransform:'uppercase',marginBottom:'14px' }}>Pricing</p>
        <h2 style={{ ...reveal(vPrice),fontSize:isMobile?'28px':'46px',fontWeight:'800',letterSpacing:isMobile?'-1px':'-2px',color:T.text,margin:'0 0 12px' }}>Try free for 7 days</h2>
        <div style={{ display:'inline-flex',alignItems:'center',gap:'6px',backgroundColor:T.emeraldDim,border:`1px solid rgba(16,185,129,0.15)`,color:T.emerald,padding:'5px 14px',borderRadius:'100px',fontSize:'12px',fontWeight:'600',marginBottom:'14px' }}>
          No charge until day 8 — cancel anytime
        </div>
        <p style={{ ...reveal(vPrice,80),color:T.textSub,fontSize:isMobile?'15px':'17px',marginBottom:'8px' }}>Bloomberg Terminal costs $25,000/year. We think that's absurd.</p>
        <p style={{ color:T.textMute,fontSize:'14px',marginBottom:isMobile?'36px':'48px' }}>Full platform access on both plans. Pay only if you love it.</p>

        <div style={{ display:'flex',gap:'16px',justifyContent:'center',flexWrap:'wrap' }}>
          {[
            { name:'Starter', price:'$19', features:['Full FDA Drug Pipeline (1,000+ drugs)','AI Summaries & Risk Scoring','Clinical Trials Feed','FDA Calendar & PDUFA Dates','Insider Trading Tracker','Portfolio Tracker','Earnings + FDA Collision Detector','Weekly Email Digest','Email Support'], color:T.blue, popular:false },
            { name:'Pro',     price:'$49', features:['Everything in Starter','Early access to new features','ML Risk Score (1–5) — coming soon','Watchlist + Price Alerts — coming soon','SEC Filing Analysis — coming soon','Unlimited Alerts','Priority Support'], color:T.purple, popular:true },
          ].map((plan,i) => (
            <div key={i} style={{ ...reveal(vPrice,i*100+200),backgroundColor:T.surface,borderRadius:'18px',padding:isMobile?'32px 24px':'40px 32px',width:isMobile?'100%':'300px',maxWidth:'360px',border:plan.popular?`1px solid ${plan.color}30`:`1px solid ${T.border}`,position:'relative',textAlign:'left' }}>
              {plan.popular && <div style={{ position:'absolute',top:'-12px',left:'50%',transform:'translateX(-50%)',backgroundColor:plan.color,color:'white',padding:'3px 14px',borderRadius:'100px',fontSize:'11px',fontWeight:'700',whiteSpace:'nowrap',letterSpacing:'0.3px' }}>Most popular</div>}
              <div style={{ fontSize:'12px',fontWeight:'700',color:T.textSub,marginBottom:'10px',letterSpacing:'0.5px',textTransform:'uppercase' }}>{plan.name}</div>
              <div style={{ fontSize:isMobile?'48px':'56px',fontWeight:'800',color:T.text,letterSpacing:'-2px',lineHeight:1,marginBottom:'4px' }}>{plan.price}<span style={{ fontSize:'15px',color:T.textSub,fontWeight:'400' }}>/mo</span></div>
              <div style={{ fontSize:'13px',color:T.emerald,fontWeight:'600',marginBottom:'20px' }}>7 days free, then {plan.price}/mo</div>
              <div style={{ borderTop:`1px solid ${T.border}`,paddingTop:'20px',marginBottom:'24px',display:'flex',flexDirection:'column',gap:'10px' }}>
                {plan.features.map((f,j) => (
                  <div key={j} style={{ display:'flex',gap:'10px',alignItems:'flex-start',fontSize:'14px',color:f.includes('coming soon')?T.textMute:T.textSub,lineHeight:1.5 }}>
                    <span style={{ color:f.includes('coming soon')?T.textMute:plan.color,fontWeight:'700',fontSize:'13px',flexShrink:0,marginTop:'1px' }}>{f.includes('coming soon')?'·':'✓'}</span>{f}
                  </div>
                ))}
              </div>
              <button onClick={onEnterDashboard} style={{ width:'100%',backgroundColor:plan.popular?plan.color:'transparent',color:plan.popular?'white':plan.color,border:`1px solid ${plan.popular?plan.color:`${plan.color}40`}`,padding:'13px',borderRadius:'10px',cursor:'pointer',fontWeight:'700',fontSize:'15px',transition:'opacity 0.2s' }} onMouseEnter={e=>e.currentTarget.style.opacity='0.85'} onMouseLeave={e=>e.currentTarget.style.opacity='1'}>
                Start free trial →
              </button>
            </div>
          ))}
        </div>
        <p style={{ color:T.textMute,fontSize:'13px',marginTop:'20px' }}>No charge for 7 days. Cancel anytime before day 8.</p>
      </div>

      {/* ── FINAL CTA ── */}
      <div style={{ position:'relative',zIndex:1,textAlign:'center',padding:isMobile?'60px 24px 80px':'96px 40px 112px',borderTop:`1px solid ${T.border}` }}>
        <div style={{ maxWidth:'600px',margin:'0 auto' }}>
          <h2 style={{ fontSize:isMobile?'32px':'54px',fontWeight:'800',letterSpacing:isMobile?'-1px':'-2px',margin:'0 0 16px',color:T.text,lineHeight:1.1 }}>
            Ready to invest<br /><span style={{ color:T.emerald }}>with an edge?</span>
          </h2>
          <p style={{ color:T.textSub,fontSize:isMobile?'15px':'18px',marginBottom:'32px',lineHeight:1.7 }}>Join investors already using AlphaBioAI to track the biotech pipeline, decode insider trades, and detect earnings collisions — before the market reacts.</p>
          <div style={{ display:'flex',gap:'12px',justifyContent:'center',flexWrap:'wrap' }}>
            <button onClick={onEnterDashboard} style={{ backgroundColor:T.emerald,color:'white',border:'none',padding:isMobile?'15px 36px':'16px 44px',borderRadius:'10px',cursor:'pointer',fontWeight:'700',fontSize:isMobile?'15px':'17px',transition:'opacity 0.2s',width:isMobile?'100%':'auto' }} onMouseEnter={e=>e.currentTarget.style.opacity='0.85'} onMouseLeave={e=>e.currentTarget.style.opacity='1'}>
              Start 7-day free trial →
            </button>
          </div>
          <p style={{ color:T.textMute,fontSize:'13px',marginTop:'14px' }}>No charge for 7 days. Cancel anytime.</p>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <footer style={{ position:'relative',zIndex:1,backgroundColor:'#050810',borderTop:`1px solid ${T.border}`,padding:isMobile?'48px 24px 32px':'56px 48px 40px' }}>
        <div style={{ maxWidth:'1040px',margin:'0 auto' }}>
          <div style={{ display:'grid',gridTemplateColumns:isMobile?'1fr 1fr':'2fr 1fr 1fr 1fr 1fr',gap:isMobile?'32px':'40px',marginBottom:'48px' }}>
            <div style={{ gridColumn:isMobile?'1 / -1':'auto' }}>
              <div style={{ display:'flex',alignItems:'center',gap:'10px',marginBottom:'14px' }}>
                <LogoMark size={28} />
                <span style={{ fontSize:'15px',fontWeight:'700',color:T.text }}>Alpha<span style={{ color:T.emerald }}>Bio</span>AI</span>
              </div>
              <p style={{ color:T.textMute,fontSize:'13px',lineHeight:1.7,margin:0,maxWidth:'220px' }}>AI-powered biotech investment intelligence at $19/month.</p>
            </div>
            {[
              { title:'Product',   links:['FDA Drug Approvals','Clinical Trials','Insider Trading','FDA Failures','Earnings Collider','Portfolio Tracker'] },
              { title:'Use Cases', links:['Retail Investors','Biotech Enthusiasts','Financial Advisors','Medical Professionals','Portfolio Managers'] },
              { title:'Resources', links:['How It Works','Pricing','FAQ'] },
              { title:'Company',   links:['About Us','Contact','Privacy Policy','Terms of Service','Legal Disclaimer'] },
            ].map((col,i) => (
              <div key={i}>
                <div style={{ fontSize:'11px',fontWeight:'700',color:T.text,letterSpacing:'0.8px',textTransform:'uppercase',marginBottom:'14px' }}>{col.title}</div>
                {col.links.map((link,j) => {
                  const pm = {'About Us':'about','Contact':'contact','Privacy Policy':'privacy','Terms of Service':'terms','Legal Disclaimer':'disclaimer','FAQ':'faq','How It Works':'howitworks','Pricing':'pricing'};
                  return (
                    <div key={j} style={{ fontSize:'13px',color:T.textMute,padding:'4px 0',cursor:'pointer',transition:'color 0.15s' }}
                      onClick={() => {
                        if (pm[link] && ['about','contact','privacy','terms','disclaimer','faq'].includes(pm[link])) onNavigate(pm[link]);
                        else if (pm[link]) document.getElementById(pm[link])?.scrollIntoView({behavior:'smooth'});
                        else document.getElementById(col.title==='Product'?'features':'pricing')?.scrollIntoView({behavior:'smooth'});
                      }}
                      onMouseEnter={e=>e.currentTarget.style.color=T.text}
                      onMouseLeave={e=>e.currentTarget.style.color=T.textMute}>{link}</div>
                  );
                })}
              </div>
            ))}
          </div>
          <div style={{ borderTop:`1px solid ${T.border}`,paddingTop:'24px',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:'8px' }}>
            <span style={{ color:T.textMute,fontSize:'13px' }}>© 2026 AlphaBioAI. All rights reserved.</span>
            <span style={{ color:T.textMute,fontSize:'13px' }}>Not financial advice.</span>
          </div>
        </div>
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; }
        @keyframes ticker { 0%{transform:translateX(0);} 100%{transform:translateX(-50%);} }
        input::placeholder { color: #3d5070; }
        ::-webkit-scrollbar{width:5px;} ::-webkit-scrollbar-track{background:#080b14;} ::-webkit-scrollbar-thumb{background:#1a2540;border-radius:3px;}
      `}</style>
    </div>
  );
}